import React from 'react';
import { Link } from 'react-router-dom';
import { Fuel, BarChart2, MapPin, TrendingUp, Building2, Zap, Lock } from 'lucide-react';

// Sample chart image for preview purposes
const sampleChartImage = '/images/sample-dashboard-chart.png';

export default function DashboardPreview() {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Dashboard & Analytics</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Unlock powerful insights and manage your gas station portfolio with our comprehensive analytics dashboard.
        </p>
      </div>

      {/* Preview Section */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600"></div>
        
        {/* Mock Dashboard Header */}
        <div className="flex justify-between items-center mb-8 border-b pb-4">
          <div className="flex items-center gap-3">
            <Fuel className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold">Your Dashboard</h2>
          </div>
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Preview Mode
          </div>
        </div>

        {/* Blurred Dashboard Content */}
        <div className="relative">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              { label: 'Portfolio Value', value: '$8.2M', trend: '+12%', icon: Building2 },
              { label: 'Valuations Completed', value: '24', trend: '+3', icon: BarChart2 },
              { label: 'Performance Score', value: '82/100', trend: '+5', icon: TrendingUp }
            ].map((stat, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
                    <p className="text-2xl font-bold">{stat.value}</p>
                  </div>
                  <div className="bg-blue-100 p-2 rounded-full">
                    <stat.icon className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-green-600 text-sm mt-2 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  {stat.trend} this quarter
                </p>
              </div>
            ))}
          </div>

          {/* Sample Chart with Blur Effect */}
          <div className="mb-8 relative">
            <h3 className="text-xl font-semibold mb-4">Comparative Analysis</h3>
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative">
              <img 
                src={sampleChartImage} 
                alt="Sample dashboard chart" 
                className="w-full h-full object-cover opacity-50"
                onError={(e) => {
                  // Fallback if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YxZjVmOSIvPjx0ZXh0IHg9IjQwMCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMzIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5NGE4YmUiPkRhc2hib2FyZCBDaGFydCBQcmV2aWV3PC90ZXh0Pjwvc3ZnPg==';
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                <div className="text-center">
                  <Lock className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                  <p className="text-lg font-semibold">Sign in to view detailed analytics</p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Preview Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="bg-blue-100 p-3 rounded-full w-fit mb-4">
                <BarChart2 className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Performance Analytics</h3>
              <p className="text-gray-600 mb-4">
                Track key metrics like fuel volume, C-store sales, and profitability over time with interactive charts.
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center">
                  <Zap className="w-4 h-4 text-green-500 mr-2" />
                  <span>Year-over-year performance tracking</span>
                </li>
                <li className="flex items-center">
                  <Zap className="w-4 h-4 text-green-500 mr-2" />
                  <span>Customizable KPI dashboards</span>
                </li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="bg-purple-100 p-3 rounded-full w-fit mb-4">
                <MapPin className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Competitive Analysis</h3>
              <p className="text-gray-600 mb-4">
                Benchmark your properties against similar locations and identify improvement opportunities.
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center">
                  <Zap className="w-4 h-4 text-green-500 mr-2" />
                  <span>Competitor mapping and insights</span>
                </li>
                <li className="flex items-center">
                  <Zap className="w-4 h-4 text-green-500 mr-2" />
                  <span>Market share analysis</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-lg p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Ready to unlock your dashboard?</h3>
          <p className="mb-6 max-w-2xl mx-auto">
            Create an account to access all analytics features, save property valuations, and track market movement.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/valuation"
              className="bg-white text-blue-700 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors"
            >
              Start Your First Valuation
            </Link>
            <Link
              to="/pricing"
              className="bg-blue-600 text-white border border-white px-8 py-3 rounded-lg font-bold hover:bg-blue-800 transition-colors"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">Dashboard Features</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              title: 'Valuation History',
              description: 'Access your complete history of property valuations with detailed metrics and comparisons.',
              icon: <Building2 className="w-6 h-6 text-blue-600" />
            },
            {
              title: 'Comparative Analysis',
              description: 'Compare your properties against similar sites and industry benchmarks.',
              icon: <BarChart2 className="w-6 h-6 text-blue-600" />
            },
            {
              title: 'Performance Metrics',
              description: 'Track revenue, profitability, and operational efficiency metrics over time.',
              icon: <TrendingUp className="w-6 h-6 text-blue-600" />
            }
          ].map((feature, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md">
              <div className="bg-blue-50 p-3 rounded-full w-fit mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">Start tracking your performance today</h2>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
          Access your valuation history, run comparative analyses, and make data-driven decisions about your portfolio.
        </p>
        <Link
          to="/valuation"
          className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors inline-block"
        >
          Start Your First Valuation
        </Link>
      </div>
    </div>
  );
}
